import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../context/AuthContext'
import { LOYALTY_SEARCH_LIMIT, cardUrl, createLoyaltyCard, searchLoyaltyCards } from '../../services/loyalty'
import type { LoyaltyCard } from '../../types'
import { QrCode } from '../QrCode'
import { XIcon } from '../icons'
import './ConfirmDialog.css'
import './LoyaltyDialog.css'

/**
 * Kasada müşteri sadakat (30 Eki 2026): kartı kod, telefon ya da ad soyadla
 * bul (1 Kas) ya da yeni kart aç,
 * damgaları gör, kart doluysa ödülü bu satışta kullan. Seçilen kart satışa
 * eklenir; damga ödeme alınınca sunucuda eklenir (supabase/sadakat.sql).
 */
export function LoyaltyDialog({
  current,
  onAttach,
  onDetach,
  onClose,
}: {
  current: { card: LoyaltyCard; useReward: boolean } | null
  onAttach: (card: LoyaltyCard, useReward: boolean) => void
  onDetach: () => void
  onClose: () => void
}) {
  const { tenantId, loyalty } = useAuth()
  const [mode, setMode] = useState<'find' | 'new'>('find')
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<{ cards: LoyaltyCard[]; more: boolean } | null>(null)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [card, setCard] = useState<LoyaltyCard | null>(current?.card ?? null)
  const [created, setCreated] = useState(false)
  const [useReward, setUseReward] = useState(current?.useReward ?? false)

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onClose])

  const ready = card !== null && card.stamps >= loyalty.target

  const pick = (c: LoyaltyCard) => {
    setCard(c)
    setResults(null)
    setUseReward(c.stamps >= loyalty.target)
  }

  const find = async () => {
    if (!tenantId || query.trim().length < 2) return
    setBusy(true)
    setError(null)
    setResults(null)
    try {
      const found = await searchLoyaltyCards(tenantId, query)
      if (found.cards.length === 0) setError('Kart bulunamadı. Yazılanı kontrol edin ya da yeni kart açın.')
      else if (found.cards.length === 1 && !found.more) pick(found.cards[0])
      else setResults(found)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const create = async () => {
    if (!tenantId) return
    setBusy(true)
    setError(null)
    try {
      setCard(await createLoyaltyCard(tenantId, name, phone))
      setCreated(true)
      setUseReward(false)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  // Sepet panelinin içinden açılıyor; ürün listesinin altında kalmasın diye
  // sayfanın en üstüne çiziliyor.
  return createPortal(
    <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="loy-title" onClick={onClose}>
      <div className="dlg__card dlg__card--x loy" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat">
          <XIcon />
        </button>
        <h2 id="loy-title">Müşteri sadakat</h2>

        {card ? (
          <>
            <div className="loy__card">
              <div className="loy__head">
                <span className="loy__label">Kart kodu</span>
                <b className="loy__code">{card.code}</b>
                {card.name && <span className="loy__name">{card.name}</span>}
              </div>
              <div className="loy__stamps" aria-label={`${card.stamps} / ${loyalty.target} damga`}>
                {Array.from({ length: loyalty.target }, (_, i) => (
                  <i key={i} className={i < card.stamps ? 'is-on' : ''} />
                ))}
              </div>
              <p className="loy__count">
                <b>{Math.min(card.stamps, loyalty.target)}</b> / {loyalty.target} damga
                {card.stamps > loyalty.target && <> (+{card.stamps - loyalty.target})</>}
              </p>
            </div>

            {created && (
              <div className="loy__qr">
                <QrCode value={cardUrl(card.token)} size={150} label="Müşterinin kart sayfası" />
                <p>
                  Müşteri bu karekodu telefonuyla okutsun: kartını ve damgalarını kendi telefonunda görür. Sonraki
                  gelişinde kodunu, telefonunu ya da adını kasada söyler.
                </p>
              </div>
            )}

            {ready && (
              <label className="loy__reward">
                <input type="checkbox" checked={useReward} onChange={(e) => setUseReward(e.target.checked)} />
                <span>
                  <b>Ödül hazır: %{loyalty.rewardPercent} indirim</b>
                  <small>Bu satışta kullanılsın; kart {loyalty.target} damga düşer.</small>
                </span>
              </label>
            )}

            <div className="dlg__buttons dlg__buttons--even">
              {current ? (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    onDetach()
                    onClose()
                  }}
                >
                  Satıştan çıkar
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => {
                    setCard(null)
                    setCreated(false)
                    setQuery('')
                  }}
                >
                  Başka kart
                </button>
              )}
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  onAttach(card, ready && useReward)
                  onClose()
                }}
              >
                Satışa ekle
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="loy__tabs" role="tablist">
              <button type="button" role="tab" aria-selected={mode === 'find'} className={mode === 'find' ? 'is-on' : ''} onClick={() => setMode('find')}>
                Kart bul
              </button>
              <button type="button" role="tab" aria-selected={mode === 'new'} className={mode === 'new' ? 'is-on' : ''} onClick={() => setMode('new')}>
                Yeni kart
              </button>
            </div>

            {mode === 'find' ? (
              <form
                className="loy__form"
                onSubmit={(e) => {
                  e.preventDefault()
                  void find()
                }}
              >
                <label className="dlg__field">
                  Kod, telefon ya da ad soyad
                  <input
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value)
                      setResults(null)
                    }}
                    placeholder="4821, telefon (son 4 hane yeter) ya da Ayşe Yılmaz"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                  />
                </label>
                {error && <p className="loy__err">{error}</p>}
                {results && (
                  <div className="loy__results" role="list" aria-label="Bulunan kartlar">
                    <p className="loy__label">
                      {results.more ? `${LOYALTY_SEARCH_LIMIT}'den fazla kart var, ilk ${LOYALTY_SEARCH_LIMIT}'si` : `${results.cards.length} kart bulundu, seçin`}
                    </p>
                    {results.more && (
                      <p className="loy__more">Aradığınız yoksa soyadını ya da telefonun son hanelerini de yazın.</p>
                    )}
                    {results.cards.map((c) => (
                      <button key={c.id} type="button" role="listitem" className="loy__hit" onClick={() => pick(c)}>
                        <span className="loy__hitmain">
                          <b>{c.name || 'Adsız kart'}</b>
                          {c.phone && <small>{maskPhone(c.phone)}</small>}
                        </span>
                        <span className="loy__hitside">
                          <b>{c.code}</b>
                          <small>
                            {Math.min(c.stamps, loyalty.target)} / {loyalty.target} damga
                          </small>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="dlg__buttons">
                  <button type="submit" className="btn btn--primary" disabled={busy || query.trim().length < 2}>
                    {busy ? 'Aranıyor…' : 'Kartı bul'}
                  </button>
                </div>
              </form>
            ) : (
              <form
                className="loy__form"
                onSubmit={(e) => {
                  e.preventDefault()
                  void create()
                }}
              >
                <label className="dlg__field">
                  Ad soyad
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ayşe Yılmaz" autoComplete="off" />
                </label>
                <label className="dlg__field">
                  Telefon
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="05xx xxx xx xx" autoComplete="off" />
                </label>
                <p className="loy__hint">Biri yeter. Müşteri sonraki gelişinde kodunu, telefonunu ya da adını söyler.</p>
                {error && <p className="loy__err">{error}</p>}
                <div className="dlg__buttons">
                  <button type="submit" className="btn btn--primary" disabled={busy || (!name.trim() && !phone.trim())}>
                    {busy ? 'Açılıyor…' : 'Kart aç'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** Listede telefonun yalnız sonu görünsün: "•••• 45 67". */
function maskPhone(p: string): string {
  const d = p.replace(/\D/g, '')
  return d.length < 4 ? p : `•••• ${d.slice(-4, -2)} ${d.slice(-2)}`
}
